import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, FileText, Plus } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
  const [formData, setFormData] = useState({
    case_name: "",
    patient_name: "",
    date_of_injury: ""
  });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const handleCreateCase = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/cases`, formData);
      toast.success("Case created successfully");
      // Navigate to the case detail page to upload files
      navigate(`/case/${response.data.id}`);
    } catch (error) {
      console.error("Error creating case:", error);
      toast.error("Failed to create case");
    } finally {
      setCreating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
            Upload New Records
          </h1>
          <p className="text-lg" style={{ color: '#F5F7FA' }}>
            Create a new case and upload medical records and bills
          </p>
        </div>

        {/* Upload Zone Card */}
        <Card className="glass-card">
          <CardHeader>
            <div className="flex items-center space-x-3 mb-2">
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(36, 84, 81, 0.15)' }}
              >
                <FileText className="h-6 w-6" style={{ color: '#245451' }} />
              </div>
              <div>
                <CardTitle style={{ color: '#245451' }}>Create New Case</CardTitle>
                <CardDescription style={{ color: '#64748B' }}>
                  Enter case details to begin medical record analysis
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateCase} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="case_name">Case Name</Label>
                <Input
                  id="case_name"
                  placeholder="e.g., Smith v. Jones"
                  value={formData.case_name}
                  onChange={(e) => setFormData({ ...formData, case_name: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="patient_name">Patient Name</Label>
                <Input
                  id="patient_name"
                  placeholder="e.g., John Smith"
                  value={formData.patient_name}
                  onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="date_of_injury">Date of Injury</Label>
                <Input
                  id="date_of_injury"
                  type="date"
                  value={formData.date_of_injury}
                  onChange={(e) => setFormData({ ...formData, date_of_injury: e.target.value })}
                  required
                />
              </div>

              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base"
                  style={{ backgroundColor: '#245451' }}
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-5 w-5" />
                      Create Case & Upload Files
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="glass-card mt-6">
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#245451' }}>Accepted File Types</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 text-sm" style={{ color: '#64748B' }}>
              <div>
                <p className="font-semibold mb-2" style={{ color: '#245451' }}>Medical Records:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>PDF documents (.pdf)</li>
                  <li>Word documents (.docx)</li>
                  <li>Images (.jpg, .jpeg, .png)</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2" style={{ color: '#245451' }}>Bills & Ledgers:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>PDF documents (.pdf)</li>
                  <li>Excel spreadsheets (.xlsx, .xls)</li>
                  <li>Images (.jpg, .jpeg, .png)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default UploadPage;